/**
 * @fileoverview CategoryGroup — top-level band (Imported / Excluded / Failed /
 * Removed) that wraps a stack of CategoryAccordions.
 * @module import-editor/dryRunSummary
 *
 * Responsibilities:
 * - Render a coloured header with the group label and total count.
 * - Stack its children (CategoryAccordions) underneath.
 *
 * Dependencies:
 * - React
 *
 * Architecture:
 * - Presentation-only. No state.
 */

/**
 * @component CategoryGroup
 *
 * Purpose:
 * - Visually group related accordions so the user can scan totals at a glance.
 *
 * Props:
 * - kind:  group key (drives colour)
 * - label: user-facing heading
 * - count: sum of children counts
 * - children: the stack of accordions
 *
 * Behavior:
 * - Pure render.
 */

import type { ReactNode } from 'react';
import type { CategoryGroupKind } from './types';

const GROUP_STYLES: Record<CategoryGroupKind, { bar: string; text: string }> = {
  imported: { bar: 'bg-emerald-500', text: 'text-emerald-800' },
  excluded: { bar: 'bg-amber-500',   text: 'text-amber-800' },
  failed:   { bar: 'bg-red-500',     text: 'text-red-800' },
  removed:  { bar: 'bg-orange-500',  text: 'text-orange-800' },
};

export interface CategoryGroupProps {
  kind: CategoryGroupKind;
  label: string;
  count: number;
  children?: ReactNode;
}

export function CategoryGroup(props: CategoryGroupProps): JSX.Element {
  const { kind, label, count, children } = props;
  const style = GROUP_STYLES[kind];
  return (
    <section aria-labelledby={`catgroup-${kind}-header`} className="space-y-2">
      <header
        id={`catgroup-${kind}-header`}
        className="flex items-center gap-2 px-2 py-1.5 border-l-4 border-transparent"
        style={{ borderLeftColor: 'currentColor' }}
      >
        <span className={`inline-block h-2 w-2 rounded-full ${style.bar}`} aria-hidden="true" />
        <h3 className={`text-sm font-semibold ${style.text}`}>{label}</h3>
        <span className="ml-auto text-xs text-gray-500 tabular-nums">
          {count.toLocaleString()} row{count === 1 ? '' : 's'}
        </span>
      </header>
      <div className="space-y-1.5 pl-1">{children}</div>
    </section>
  );
}

export default CategoryGroup;
